import React, {useEffect, useState} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, flexDirection, mb, mr, mt, onDesktop, w100} from "@pg-design/helpers-css";
import {IRange, Range, RangeColorVariant} from "@pg-design/range-module";
import {Text} from "@pg-design/text-module";

import {CalculatorValueType} from "../types/CalculatorValueType";
import {ICalculatorOptions} from "../types/ICalculatorOptions";
import {getCalculatorSelectOptions} from "../utils/get_calculator_select_options";
import {CalculatorBaseInput} from "./calculator_inputs/CalculatorBaseInput";
import {CalculatorPercentageInput} from "./calculator_inputs/CalculatorPercentageInput";
import {CalculatorSelectInput} from "./calculator_inputs/CalculatorSelectInput";

interface IProps {
    rangeOptions: ICalculatorOptions;
    inputOnTitlePlane?: boolean;
    value: number;
    price?: number;
    updateValue: (type: CalculatorValueType, value: number) => void;
    hideLabels?: boolean;
    currency?: string;
}

export const RangeWithInput = (props: IProps) => {
    const {rangeOptions, inputOnTitlePlane, value, price, updateValue, currency} = props;

    const [inputValue, setInputValue] = useState<number>(value);

    const baseInputLabel = rangeOptions.valueType === CalculatorValueType.AGE ? "lat" : currency || "zł";

    const selectOptions = getCalculatorSelectOptions(rangeOptions.values, rangeOptions.step);
    const theme = useTheme();

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const onChange = (value: number) => {
        updateValue(props.rangeOptions.valueType, value);
    };

    const updateInputState = (value: number) => {
        setInputValue(value);
    };

    const onInvalidValue = (value?: number) => {
        updateValue(props.rangeOptions.valueType, value || props.rangeOptions.values.init || 0);
    };

    const renderInput = () => {
        switch (rangeOptions.valueType) {
            case CalculatorValueType.CONTRIBUTION:
                return (
                    <CalculatorPercentageInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        price={price as number}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.allowedInputOverride ?? (rangeOptions.values.upper as number)}
                        onInvalidValue={onInvalidValue}
                        updateValue={updateValue}
                        updateInputState={updateInputState}
                        dataTestId={rangeOptions.name}
                    />
                );
            case CalculatorValueType.PERIOD:
                return (
                    <CalculatorSelectInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        options={selectOptions}
                        updateValue={updateValue}
                        wrapperStyle={props.inputOnTitlePlane ? undefined : inputStyle}
                        dataTestId={rangeOptions.name}
                    />
                );
            case CalculatorValueType.AGE:
            case CalculatorValueType.PRICE:
            case CalculatorValueType.EARNINGS:
            default:
                return (
                    <CalculatorBaseInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.allowedInputOverride ?? (rangeOptions.values.upper as number)}
                        updateValue={updateValue}
                        label={baseInputLabel}
                        wrapperStyle={props.inputOnTitlePlane ? undefined : inputStyle}
                        onInvalidValue={onInvalidValue}
                        dataTestId={rangeOptions.name}
                    />
                );
        }
    };

    return (
        <div>
            <div css={titleWrapper}>
                <div>
                    <Text variant="headline_6" as="p">
                        {rangeOptions.title}
                    </Text>
                    {rangeOptions.desc && (
                        <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                            {rangeOptions.desc}
                        </Text>
                    )}
                </div>
                {inputOnTitlePlane && <div css={titleInputStyle}>{renderInput()}</div>}
            </div>
            <div css={rangeWithInputContainer}>
                {!inputOnTitlePlane && renderInput()}

                <RangeWrapper areLabelsHidden={props.hideLabels} inputOnTitlePlane={props.inputOnTitlePlane}>
                    <Range
                        name={rangeOptions.name}
                        onChange={(_: string, value: IRange<number>) => {
                            if (value.upper || value.upper === 0) {
                                onChange(value.upper);
                            }
                        }}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.values.upper as number}
                        step={rangeOptions.step}
                        value={{
                            lower:
                                rangeOptions.values.upper && rangeOptions.allowedInputOverride && value > rangeOptions.values.upper
                                    ? rangeOptions.values.upper
                                    : value
                        }}
                        unitType={rangeOptions.uniteType}
                        useDynamicChange
                        hideLabels={props.hideLabels}
                        colorVariant={RangeColorVariant.LAVENDER}
                    />
                </RangeWrapper>
            </div>
        </div>
    );
};

const titleWrapper = css`
    ${flex("center", "space-between")};
    ${mb(1)};
    gap: ${calculateRemSize(5)};
`;

const RangeWrapper = styled.div<{areLabelsHidden?: boolean; inputOnTitlePlane?: boolean}>`
    ${w100};
    ${mt(2)};
    ${(props) => (props.areLabelsHidden ? mb(4) : null)};

    ${(props) => onDesktop(props.inputOnTitlePlane ? mr(0) : mr(4), mt(0))};
`;

const rangeWithInputContainer = css`
    ${flexAbsoluteCenter};
    ${flexDirection("column")};

    ${onDesktop(flexDirection("row-reverse"))};
`;

const inputStyle = css`
    width: 100%;

    ${onDesktop(css`
        max-width: ${calculateRemSize(15)};
    `)};
`;

const titleInputStyle = css`
    ${w100};
    max-width: ${calculateRemSize(14)};

    ${onDesktop(css`
        max-width: ${calculateRemSize(20)};
    `)};
`;
import React, {useEffect, useState} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, flexDirection, mb, mr, mt, onDesktop, w100} from "@pg-design/helpers-css";
import {IRange, Range, RangeColorVariant} from "@pg-design/range-module";
import {Text} from "@pg-design/text-module";

import {CalculatorValueType} from "../types/CalculatorValueType";
import {ICalculatorOptions} from "../types/ICalculatorOptions";
import {getCalculatorSelectOptions} from "../utils/get_calculator_select_options";
import {CalculatorBaseInput} from "./calculator_inputs/CalculatorBaseInput";
import {CalculatorPercentageInput} from "./calculator_inputs/CalculatorPercentageInput";
import {CalculatorSelectInput} from "./calculator_inputs/CalculatorSelectInput";

interface IProps {
    rangeOptions: ICalculatorOptions;
    inputOnTitlePlane?: boolean;
    value: number;
    price?: number;
    updateValue: (type: CalculatorValueType, value: number) => void;
    hideLabels?: boolean;
    currency?: string;
}

export const RangeWithInput = (props: IProps) => {
    const {rangeOptions, inputOnTitlePlane, value, price, updateValue, currency} = props;

    const [inputValue, setInputValue] = useState<number>(value);

    const baseInputLabel = rangeOptions.valueType === CalculatorValueType.AGE ? "lat" : currency || "zł";

    const selectOptions = getCalculatorSelectOptions(rangeOptions.values, rangeOptions.step);
    const theme = useTheme();

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const onChange = (value: number) => {
        updateValue(props.rangeOptions.valueType, value);
    };

    const updateInputState = (value: number) => {
        setInputValue(value);
    };

    const onInvalidValue = (value?: number) => {
        updateValue(props.rangeOptions.valueType, value || props.rangeOptions.values.init || 0);
    };

    const renderInput = () => {
        switch (rangeOptions.valueType) {
            case CalculatorValueType.CONTRIBUTION:
                return (
                    <CalculatorPercentageInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        price={price as number}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.allowedInputOverride ?? (rangeOptions.values.upper as number)}
                        onInvalidValue={onInvalidValue}
                        updateValue={updateValue}
                        updateInputState={updateInputState}
                        dataTestId={rangeOptions.name}
                    />
                );
            case CalculatorValueType.PERIOD:
                return (
                    <CalculatorSelectInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        options={selectOptions}
                        updateValue={updateValue}
                        wrapperStyle={props.inputOnTitlePlane ? undefined : inputStyle}
                        dataTestId={rangeOptions.name}
                    />
                );
            case CalculatorValueType.AGE:
            case CalculatorValueType.PRICE:
            case CalculatorValueType.EARNINGS:
            default:
                return (
                    <CalculatorBaseInput
                        value={inputValue}
                        valueType={rangeOptions.valueType}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.allowedInputOverride ?? (rangeOptions.values.upper as number)}
                        updateValue={updateValue}
                        label={baseInputLabel}
                        wrapperStyle={props.inputOnTitlePlane ? undefined : inputStyle}
                        onInvalidValue={onInvalidValue}
                        dataTestId={rangeOptions.name}
                    />
                );
        }
    };

    return (
        <div>
            <div css={titleWrapper}>
                <div>
                    <Text variant="headline_6" as="p">
                        {rangeOptions.title}
                    </Text>
                    {rangeOptions.desc && (
                        <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                            {rangeOptions.desc}
                        </Text>
                    )}
                </div>
                {inputOnTitlePlane && <div css={titleInputStyle}>{renderInput()}</div>}
            </div>
            <div css={rangeWithInputContainer}>
                {!inputOnTitlePlane && renderInput()}

                <RangeWrapper areLabelsHidden={props.hideLabels} inputOnTitlePlane={props.inputOnTitlePlane}>
                    <Range
                        name={rangeOptions.name}
                        onChange={(_: string, value: IRange<number>) => {
                            if (value.upper || value.upper === 0) {
                                onChange(value.upper);
                            }
                        }}
                        min={rangeOptions.values.lower as number}
                        max={rangeOptions.values.upper as number}
                        step={rangeOptions.step}
                        value={{
                            lower:
                                rangeOptions.values.upper && rangeOptions.allowedInputOverride && value > rangeOptions.values.upper
                                    ? rangeOptions.values.upper
                                    : value
                        }}
                        unitType={rangeOptions.uniteType}
                        useDynamicChange
                        hideLabels={props.hideLabels}
                        colorVariant={RangeColorVariant.LAVENDER}
                    />
                </RangeWrapper>
            </div>
        </div>
    );
};

const titleWrapper = css`
    ${flex("center", "space-between")};
    ${mb(1)};
    gap: ${calculateRemSize(5)};
`;

const RangeWrapper = styled.div<{areLabelsHidden?: boolean; inputOnTitlePlane?: boolean}>`
    ${w100};
    ${mt(2)};
    ${(props) => (props.areLabelsHidden ? mb(4) : null)};

    ${(props) => onDesktop(props.inputOnTitlePlane ? mr(0) : mr(4), mt(0))};
`;

const rangeWithInputContainer = css`
    ${flexAbsoluteCenter};
    ${flexDirection("column")};

    ${onDesktop(flexDirection("row-reverse"))};
`;

const inputStyle = css`
    width: 100%;

    ${onDesktop(css`
        max-width: ${calculateRemSize(15)};
    `)};
`;

const titleInputStyle = css`
    ${w100};
    max-width: ${calculateRemSize(14)};

    ${onDesktop(css`
        max-width: ${calculateRemSize(20)};
    `)};
`;
